// src/utils/profileData.js

/**
 * Собирает данные профиля для обновления через PATCH /profiles
 * @param {object} context - Контекст компонента с необходимыми данными
 * @returns {object} Payload для UPDATE_DELIVERY_PROFILE
 */
export function buildProfileUpdatePayload(context) {
  const {
    orderData,
    additionalInfo,
    positionData,
    activeDeliveryType,
    selectedDeliveryService,
    selectedPickpointInfo,
    activePaymentType,
    selectedProfileId,
    profileAddressComment,
    getCountryCode,
    isHiddenCommentBlock,
    courierCity,
  } = context

  // Базовые данные профиля
  const profileData = {
    FIO: orderData.FIO,
    PHONE: orderData.PHONE.replace(/[^0-9+]/g, ''),
    EMAIL: orderData.EMAIL,
    COUNTRY: getCountryCode,
    DELIVERY_TYPE: activeDeliveryType.ID,
  }

  // Добавляем данные в зависимости от типа доставки
  if (activeDeliveryType.ID === 'PICK_POINT') {
    // ===== ПВЗ (Пункт выдачи заказов) =====
    profileData.AREA = positionData.AREA_NAME
    profileData.CITY = positionData.CITY_NAME
    profileData.PICK_POINT_SERVICE = selectedPickpointInfo.TYPE
    profileData.PICK_POINT_CODE = selectedPickpointInfo.CODE
    profileData.STREET = selectedPickpointInfo.ADDRESS

    // Специфичная логика для Польши
    if (getCountryCode === 'PL') {
      delete profileData.STREET
      profileData.ADDRESS = selectedPickpointInfo.ADDRESS
      const addPart = selectedPickpointInfo.ADDRESS.split(' ')
      profileData.POSTINDEX = addPart[0]
      profileData.BUILD_APART_NUM = addPart[addPart.length - 1]

      // Добавляем дополнительные поля адреса если есть
      if (orderData.PROFILE && Object.keys(orderData.PROFILE).length > 0) {
        Object.assign(profileData, orderData.PROFILE)
        // Восстанавливаем актуальные значения, которые могли быть перезаписаны старыми из PROFILE
        profileData.FIO = orderData.FIO
        profileData.EMAIL = orderData.EMAIL
        profileData.PHONE = orderData.PHONE.replace(/[^0-9+]/g, '')
      }
    }
  } else {
    // ===== КУРЬЕРСКАЯ / ПОЧТОВАЯ доставка =====
    profileData.DELIVERY_SERVICE = selectedDeliveryService

    // Копируем все данные из PROFILE
    Object.assign(profileData, orderData.PROFILE)
    // Восстанавливаем актуальные значения, которые могли быть перезаписаны старыми из PROFILE
    profileData.FIO = orderData.FIO
    profileData.EMAIL = orderData.EMAIL
    profileData.PHONE = orderData.PHONE.replace(/[^0-9+]/g, '')
    profileData.DELIVERY_TYPE = activeDeliveryType.ID

    // Для Италии не добавляем AREA
    if (getCountryCode !== 'IT') {
      profileData.AREA = positionData.AREA_NAME
    }

    profileData.CITY = courierCity ?? positionData.CITY_NAME
    profileData.COUNTRY = getCountryCode

    // Комментарий к адресу (для некоторых стран)
    if (isHiddenCommentBlock && profileAddressComment) {
      profileData.COMMENT = profileAddressComment
    }
  }

  // Флаг ручного ввода адреса
  if (window.addressManual) {
    profileData.MANUAL = 1
  }

  // Добавляем дополнительные поля (FAKTURA и т.д.)
  if (
    additionalInfo.PROFILE &&
    Object.keys(additionalInfo.PROFILE).length > 0
  ) {
    Object.assign(profileData, additionalInfo.PROFILE)
  }

  // Формируем LAST_DATA (последний выбор доставки и оплаты)
  const lastData = {
    paymentId: activePaymentType,
  }

  if (activeDeliveryType.ID === 'PICK_POINT') {
    lastData.deliveryId = selectedPickpointInfo.TYPE
    lastData.pickPoint = selectedPickpointInfo.CODE
  } else {
    lastData.deliveryId = selectedDeliveryService
  }

  // Итоговый payload
  const payload = {
    PROFILE: profileData,
    LAST_DATA: lastData,
    profileId: selectedProfileId,
  }

  return payload
}

/**
 * Валидирует данные профиля перед отправкой
 * @param {object} payload - Payload для проверки
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export function validateProfilePayload(payload) {
  const errors = []

  // Обязательные поля
  if (!payload.PROFILE.FIO || !payload.PROFILE.FIO.trim()) {
    errors.push('FIO is required')
  }
  if (!payload.PROFILE.PHONE || !payload.PROFILE.PHONE.trim()) {
    errors.push('PHONE is required')
  }
  if (!payload.PROFILE.EMAIL || !payload.PROFILE.EMAIL.trim()) {
    errors.push('EMAIL is required')
  }
  if (!payload.PROFILE.COUNTRY) {
    errors.push('COUNTRY is required')
  }
  if (!payload.PROFILE.DELIVERY_TYPE) {
    errors.push('DELIVERY_TYPE is required')
  }

  // Для ПВЗ нужны дополнительные поля
  if (payload.PROFILE.DELIVERY_TYPE === 'PICK_POINT') {
    if (!payload.PROFILE.PICK_POINT_CODE) {
      errors.push('PICK_POINT_CODE is required for PICK_POINT delivery')
    }
    if (!payload.PROFILE.PICK_POINT_SERVICE) {
      errors.push('PICK_POINT_SERVICE is required for PICK_POINT delivery')
    }
  }

  // LAST_DATA
  if (!payload.LAST_DATA.deliveryId) {
    errors.push('LAST_DATA.deliveryId is required')
  }
  if (!payload.LAST_DATA.paymentId) {
    errors.push('LAST_DATA.paymentId is required')
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}
